import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX, Music } from 'lucide-react';

// Hawayein The Band - Fire On Stage
const MUSIC_URL = 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/i9lxmce7_Hawayein-The-Band-Fire-On-Stage.mp3.mpeg';
const START_TIME = 50; // Start from 50 seconds

export const MusicPlayer = () => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [showPlayer, setShowPlayer] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);
    const [showPrompt, setShowPrompt] = useState(true);
    const audioRef = useRef(null);

    useEffect(() => {
        // Show the player after a short delay
        const timer = setTimeout(() => setShowPlayer(true), 1500);
        return () => clearTimeout(timer);
    }, []);

    // Try to autoplay on first user interaction with the page
    useEffect(() => {
        const handleFirstInteraction = () => {
            if (!hasInteracted && audioRef.current) {
                setHasInteracted(true);
                audioRef.current.currentTime = START_TIME;
                audioRef.current.volume = 0.3; // Set to 30% volume for ambient feel
                audioRef.current.play()
                    .then(() => {
                        setIsPlaying(true);
                        setShowPrompt(false);
                    })
                    .catch((e) => {
                        console.log('Autoplay blocked, user needs to click play button');
                    });
            }
        };

        // Listen for any click on the page
        document.addEventListener('click', handleFirstInteraction, { once: true });
        document.addEventListener('touchstart', handleFirstInteraction, { once: true });
        document.addEventListener('scroll', handleFirstInteraction, { once: true });

        return () => {
            document.removeEventListener('click', handleFirstInteraction);
            document.removeEventListener('touchstart', handleFirstInteraction);
            document.removeEventListener('scroll', handleFirstInteraction);
        };
    }, [hasInteracted]);

    const togglePlay = () => {
        setHasInteracted(true);
        setShowPrompt(false);
        
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
                setIsPlaying(false);
            } else {
                // Set start time to 50 seconds
                if (audioRef.current.currentTime < START_TIME) {
                    audioRef.current.currentTime = START_TIME;
                }
                audioRef.current.volume = 0.3;
                audioRef.current.play()
                    .then(() => setIsPlaying(true))
                    .catch((e) => console.log('Play failed:', e));
            }
        }
    };

    const toggleMute = () => {
        if (audioRef.current) {
            audioRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };

    // Handle audio ended - loop back to start time
    const handleEnded = () => {
        if (audioRef.current) {
            audioRef.current.currentTime = START_TIME;
            audioRef.current.play();
        }
    };

    return (
        <>
            <audio 
                ref={audioRef} 
                src={MUSIC_URL} 
                preload="auto"
                onEnded={handleEnded}
            />
            
            <AnimatePresence>
                {showPlayer && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: 20 }}
                        className="fixed bottom-6 left-4 z-50"
                    >
                        {/* Prompt to click */}
                        <AnimatePresence>
                            {showPrompt && !isPlaying && (
                                <motion.div
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -10 }}
                                    className="absolute left-16 top-1/2 -translate-y-1/2 whitespace-nowrap"
                                >
                                    <div className="bg-card/90 backdrop-blur-md border border-primary/30 rounded-xl px-4 py-2 shadow-lg">
                                        <p className="text-sm font-body text-foreground flex items-center gap-2">
                                            <span className="text-primary">🎵</span>
                                            <span>Click to feel the vibe!</span>
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-0.5">Fire On Stage - Hawayein</p>
                                    </div>
                                    {/* Arrow */}
                                    <div className="absolute left-0 top-1/2 -translate-x-2 -translate-y-1/2 w-0 h-0 border-t-8 border-t-transparent border-r-8 border-r-card/90 border-b-8 border-b-transparent" />
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Main Player Button */}
                        <motion.div
                            className="relative"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            {/* Pulsing ring when not playing */}
                            {!isPlaying && (
                                <motion.div
                                    className="absolute inset-0 rounded-full bg-primary/40"
                                    animate={{
                                        scale: [1, 1.4, 1],
                                        opacity: [0.6, 0, 0.6],
                                    }}
                                    transition={{
                                        duration: 1.5,
                                        repeat: Infinity,
                                        ease: 'easeInOut',
                                    }}
                                />
                            )}
                            
                            {/* Sound waves animation when playing */}
                            {isPlaying && (
                                <>
                                    <motion.div
                                        className="absolute -inset-2 rounded-full"
                                        style={{
                                            background: 'conic-gradient(from 0deg, hsl(var(--primary)), hsl(var(--accent)), hsl(var(--amber)), hsl(var(--primary)))',
                                        }}
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                                    />
                                    {/* Inner glow */}
                                    <motion.div
                                        className="absolute inset-0 rounded-full bg-primary/20 blur-md"
                                        animate={{
                                            scale: [1, 1.2, 1],
                                            opacity: [0.3, 0.6, 0.3],
                                        }}
                                        transition={{ duration: 1, repeat: Infinity }}
                                    />
                                </>
                            )}

                            <button
                                onClick={togglePlay}
                                className={`relative w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 ${
                                    isPlaying 
                                        ? 'bg-gradient-to-br from-primary to-accent shadow-glow-md' 
                                        : 'bg-card/90 backdrop-blur-md border-2 border-primary/50 hover:border-primary shadow-lg'
                                }`}
                                aria-label={isPlaying ? 'Pause music' : 'Play music'}
                            >
                                {isPlaying ? (
                                    <div className="flex items-end gap-0.5 h-6">
                                        {[0, 0.2, 0.1, 0.3, 0.15].map((delay, i) => (
                                            <motion.div
                                                key={i}
                                                className="w-1 bg-foreground rounded-full"
                                                animate={{
                                                    height: ['30%', '100%', '50%', '80%', '30%'],
                                                }}
                                                transition={{
                                                    duration: 0.8,
                                                    repeat: Infinity,
                                                    delay: delay,
                                                    ease: 'easeInOut',
                                                }}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <Music className="w-6 h-6 text-primary" />
                                )}
                            </button>
                        </motion.div>

                        {/* Volume control - shows when playing */}
                        <AnimatePresence>
                            {isPlaying && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.8 }}
                                    className="absolute left-16 top-1/2 -translate-y-1/2 flex items-center gap-2"
                                >
                                    <button
                                        onClick={toggleMute}
                                        className="w-10 h-10 rounded-full bg-card/90 backdrop-blur-md border border-border/50 flex items-center justify-center hover:border-primary/50 transition-colors"
                                        aria-label={isMuted ? 'Unmute' : 'Mute'}
                                    >
                                        {isMuted ? (
                                            <VolumeX className="w-4 h-4 text-muted-foreground" />
                                        ) : (
                                            <Volume2 className="w-4 h-4 text-primary" />
                                        )}
                                    </button>
                                    <div className="bg-card/80 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-border/30">
                                        <p className="text-xs text-primary font-body font-medium">🔥 Fire On Stage</p>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
